import moment from 'moment';
import truncate from 'lodash/truncate';
import { MONITOR_STATUS, SENTIMENTS, VERDICT_OUTCOMES, VIEW_TYPES } from '../watchlist/constants';
import { ALERT_METRICS_LIMIT, SUMMARY_CLAMP_CHARS } from './constants';

export const monitorVerdictIdentity = monitor => monitor.latest_verdict_version ?? 0;

export const toAlert = (monitor, seen) => {
    const verdict = monitor.latest_verdict;
    if (!verdict) return null;
    const identity = monitorVerdictIdentity(monitor);
    return {
        monitorId: monitor.monitor_id,
        sessionId: monitor.session_id,
        title: monitor.title || null,
        verdict,
        lastRunAt: monitor.last_run_at ?? null,
        identity,
        sentiment: verdict.sentiment ?? null,
        flagged: verdict.should_notify === true,
        isUnread: (seen[monitor.monitor_id] ?? -1) < identity,
    };
};

// Preserve the organization's wall clock instead of converting to browser time.
const parseReportZone = value => moment.parseZone(value, moment.ISO_8601);

const alertSortKey = alert => (alert.lastRunAt ? parseReportZone(alert.lastRunAt).valueOf() : -1);

export const buildAlerts = (monitors, seen) =>
    (monitors ?? [])
        .map(monitor => toAlert(monitor, seen))
        .filter(Boolean)
        .sort((a, b) => alertSortKey(b) - alertSortKey(a));

export const isFailedCheck = alert => alert.verdict.outcome === VERDICT_OUTCOMES.ERROR;

export const needsAttention = alert =>
    !isFailedCheck(alert) && (alert.flagged || alert.sentiment === SENTIMENTS.HEADS_UP);

export const countCheckingMonitors = monitors =>
    (monitors ?? []).filter(
        monitor => monitor.status === MONITOR_STATUS.QUEUED || monitor.status === MONITOR_STATUS.RUNNING
    ).length;

export const alertBodyText = ({ verdict }, expanded = true) => {
    const body = [verdict.headline, verdict.supporting_facts].filter(Boolean).join(' ');
    return expanded ? body : truncate(body, { length: SUMMARY_CLAMP_CHARS, separator: ' ' });
};

export const alertBodyKey = ({ verdict }) =>
    verdict.outcome === VERDICT_OUTCOMES.NO_VERDICT ? 'NO_VERDICT_BODY' : null;

export const isLongSummary = alert => alertBodyText(alert).length > SUMMARY_CLAMP_CHARS;

export const alertMetrics = ({ verdict }) => {
    const items = verdict.view?.type === VIEW_TYPES.STATS ? verdict.view.items : null;
    return items?.length ? items.slice(0, ALERT_METRICS_LIMIT) : null;
};

export const alertHeadlineMetric = alert => alertMetrics(alert)?.find(item => item.delta != null) ?? null;

export const countKey = (base, count) => `${base}${count === 1 ? '_ONE' : ''}`;

export const buildWatchlistHash = (path, sessionId) =>
    sessionId ? `#${path}?session=${encodeURIComponent(sessionId)}` : `#${path}`;

export const formatAlertTime = lastRunAt => {
    if (!lastRunAt) return null;
    const at = parseReportZone(lastRunAt);
    if (!at.isValid()) return null;
    const now = moment().utcOffset(at.utcOffset());
    const elapsed = now.diff(at);
    if (elapsed >= 0 && elapsed < 60000) return { key: 'JUST_NOW' };
    if (at.isSame(now, 'day')) return { key: 'AT_TIME', data: { time: at.format('h:mm A') } };
    if (at.isSame(now.clone().subtract(1, 'day'), 'day')) return { key: 'YESTERDAY' };
    return { key: 'ON_DATE', data: { date: at.format('MMM D') } };
};

export const latestAlertTime = alerts => {
    const stamped = alerts.filter(alert => alert.lastRunAt);
    if (!stamped.length) return null;
    return stamped.reduce((latest, alert) => (alertSortKey(alert) > alertSortKey(latest) ? alert : latest)).lastRunAt;
};
